<template>
  <v-dialog v-model="internalValue" max-width="600px" persistent scrollable>
    <v-card>
      <v-card-title>
        <v-icon left color="blue">mdi-link-variant</v-icon>
        {{ $t('onboarding.attach-enrollment') }}
      </v-card-title>

      <v-divider class="mb-4"></v-divider>

      <v-card-text>
        <v-alert v-if="error" type="error" dense outlined class="mb-4">
          {{ error }}
        </v-alert>

        <!-- Enrollment type (display only) -->
        <div class="mb-4">
          <div class="text-subtitle-2 mb-2">{{ $t('type') }}</div>
          <v-chip small :color="attachType === 'organization' ? 'blue' : 'purple'" dark>
            {{ attachType === 'organization' ? $t('onboarding.organization') : $t('onboarding.customer') }}
          </v-chip>
          <div class="text-caption text--secondary mt-1">
            {{ $t('onboarding.attach.type-determined-by-enrollment') }}
          </div>
        </div>

        <!-- Info message based on type -->
        <v-alert type="info" dense outlined class="mb-4">
          <div class="mb-2">
            <strong>
              {{ attachType === 'organization' ? $t('onboarding.attach-info.title') : $t('onboarding.attach-info.title-customer') }}
            </strong>
          </div>
          <div class="text-caption">
            {{ attachType === 'organization' ? $t('onboarding.attach-info.description') : $t('onboarding.attach-info.description-customer') }}
          </div>
        </v-alert>

        <v-alert v-if="hasStripeCustomerConflict" type="warning" dense outlined class="mb-4">
          <div class="d-flex align-center">
            <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
            <div>
              <strong>{{ $t('onboarding.warnings.stripe-customer-conflict-title') }}</strong>
              <div class="text-caption mt-1">
                {{ $t('onboarding.warnings.stripe-customer-conflict-message') }}
              </div>
            </div>
          </div>
        </v-alert>

        <div class="mb-4">
          <div class="text-subtitle-2 mb-2">{{ $t('onboarding.enrollment') }}</div>
          <v-chip small color="grey" outlined>
            {{ enrollment.companyName || $t('onboarding.no-company-name') }}
          </v-chip>
          <div class="text-caption text--secondary mt-1">
            {{ enrollment.email }}
          </div>
        </div>

        <v-form ref="form" v-model="valid">
          <!-- Organization Selector (Required) -->
          <organization-select
            v-if="attachType === 'organization'"
            v-model="form.organizationId"
            :label="$t('onboarding.attach.select-organization') + ' *'"
            :rules="[v => !!v || $t('rules.field-required')]"
            outlined
            clearable
          />

          <!-- Customer Selector (Required) -->
          <customer-select
            v-if="attachType === 'customer'"
            v-model="form.customerId"
            :label="$t('onboarding.attach.select-customer') + ' *'"
            :rules="[v => !!v || $t('rules.field-required')]"
            clearable
          />
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text small @click="close" :disabled="loading">
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          color="blue"
          small
          dark
          :loading="loading || loadingOrganization || loadingCustomer"
          :disabled="!valid || hasStripeCustomerConflict || loadingOrganization || loadingCustomer"
          @click="attach"
        >
          <v-icon left small>mdi-link-variant</v-icon>
          {{ $t('onboarding.confirm-attach-button') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import PaymentOnboardingRepository from '@repository/PaymentOnboardingRepository'
import OrganizationRepository from '@repository/OrganizationRepository'
import CustomerRepository from '@repository/CustomerRepository'
import OrganizationSelect from '@/components/blocks/Select/OrganizationSelect.vue'
import CustomerSelect from '@/components/blocks/Select/CustomerSelect.vue'
import CommonMixin from '@/components/mixins/CommonMixin'

export default {
  name: 'AttachEnrollmentDialog',

  mixins: [CommonMixin],

  components: {
    OrganizationSelect,
    CustomerSelect
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    enrollment: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      valid: false,
      loading: false,
      loadingOrganization: false,
      loadingCustomer: false,
      error: null,
      attachType: 'organization', // 'organization' or 'customer'
      selectedOrganization: null,
      selectedCustomer: null,
      form: {
        organizationId: null,
        customerId: null
      }
    }
  },

  computed: {
    internalValue: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },

    hasStripeCustomerConflict() {
      // Détecte si l'enrollment a un mandat signé avec un Stripe Customer
      // ET que l'organisation/customer a un Stripe Customer différent
      if (!this.enrollment) {
        return false
      }

      const enrollmentCustomerId = this.enrollment.stripeCustomerId
      const isMandateSigned = ['signed', 'completed'].includes(this.enrollment.status)

      if (this.attachType === 'organization' && this.selectedOrganization) {
        const organizationCustomerId = this.selectedOrganization.stripeCustomerId
        return enrollmentCustomerId &&
               organizationCustomerId &&
               enrollmentCustomerId !== organizationCustomerId &&
               isMandateSigned
      }

      if (this.attachType === 'customer' && this.selectedCustomer) {
        const customerStripeCustomerId = this.selectedCustomer.stripeCustomerId
        return enrollmentCustomerId &&
               customerStripeCustomerId &&
               enrollmentCustomerId !== customerStripeCustomerId &&
               isMandateSigned
      }

      return false
    }
  },

  watch: {
    value(val) {
      if (!val) {
        this.resetForm()
      }
    },

    enrollment: {
      immediate: true,
      handler(enrollment) {
        // Set attachType based on enrollment type
        if (enrollment?.type === 'customer') {
          this.attachType = 'customer'
        } else if (enrollment?.type === 'organization') {
          this.attachType = 'organization'
        } else {
          // Fallback to organization (should not happen anymore)
          this.attachType = 'organization'
        }
      }
    },

    attachType(newType) {
      // Clear form when switching type
      this.form.organizationId = null
      this.form.customerId = null
      this.selectedOrganization = null
      this.selectedCustomer = null
      this.error = null
    },

    'form.organizationId': {
      async handler(newOrgId) {
        if (!newOrgId) {
          this.selectedOrganization = null
          return
        }

        // Charger les détails de l'organisation pour vérifier le stripeCustomerId
        this.loadingOrganization = true
        try {
          this.selectedOrganization = await OrganizationRepository.get(newOrgId)
        } catch (err) {
          this.selectedOrganization = null
        } finally {
          this.loadingOrganization = false
        }
      }
    },

    'form.customerId': {
      async handler(newCustomerId) {
        if (!newCustomerId) {
          this.selectedCustomer = null
          return
        }

        // Charger les détails du customer pour vérifier le stripeCustomerId
        this.loadingCustomer = true
        try {
          this.selectedCustomer = await CustomerRepository.get(newCustomerId)
        } catch (err) {
          this.selectedCustomer = null
        } finally {
          this.loadingCustomer = false
        }
      }
    }
  },

  methods: {
    resetForm() {
      this.attachType = 'organization'
      this.form = {
        organizationId: null,
        customerId: null
      }
      this.selectedOrganization = null
      this.selectedCustomer = null
      this.error = null
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },

    async attach() {
      if (!this.$refs.form.validate()) return

      this.loading = true
      this.error = null

      try {
        const payload = {}

        if (this.attachType === 'organization') {
          payload.organizationId = this.form.organizationId
        } else {
          payload.customerId = this.form.customerId
        }

        const result = await PaymentOnboardingRepository.attachToOrganization(this.enrollment.id, payload)

        // Afficher le message de succès selon l'action effectuée
        let successMessage = ''
        const identifier = result.accountNumber || result.evcoId

        if (this.attachType === 'organization') {
          switch (result.stripeCustomerAction) {
            case 'none':
              successMessage = this.$t('onboarding.attach-success.none', { accountNumber: identifier })
              break
            case 'copied-from-organization':
              successMessage = this.$t('onboarding.attach-success.copied-from-organization', { accountNumber: identifier })
              break
            case 'transferred-to-organization':
              successMessage = this.$t('onboarding.attach-success.transferred-to-organization', { accountNumber: identifier })
              break
            case 'replaced-with-organization':
              successMessage = this.$t('onboarding.attach-success.replaced-with-organization', { accountNumber: identifier })
              break
            default:
              successMessage = this.$t('onboarding.attach-success-with-account', { accountNumber: identifier })
          }
        } else {
          // Customer attachment
          switch (result.stripeCustomerAction) {
            case 'none':
              successMessage = this.$t('onboarding.attach-success.none-customer', { evcoId: identifier })
              break
            case 'copied-from-customer':
              successMessage = this.$t('onboarding.attach-success.copied-from-customer', { evcoId: identifier })
              break
            case 'transferred-to-customer':
              successMessage = this.$t('onboarding.attach-success.transferred-to-customer', { evcoId: identifier })
              break
            case 'replaced-with-customer':
              successMessage = this.$t('onboarding.attach-success.replaced-with-customer', { evcoId: identifier })
              break
            default:
              successMessage = this.$t('onboarding.attach-success-with-customer', { evcoId: identifier })
          }
        }

        this.$dialog?.notify?.success && this.$dialog.notify.success(successMessage)

        // Warn if Stripe sync failed
        if (result.stripeSyncError) {
          this.$dialog?.notify?.warning && this.$dialog.notify.warning(
            this.$t('onboarding.stripe-sync-error', { error: result.stripeSyncError }),
            { timeout: 10000 }
          )
        }

        this.$emit('attached', result)
        this.close()
      } catch (err) {
        // Gérer les erreurs spécifiques de l'API
        const errorCode = err.response?.data?.error
        const errorContext = err.response?.data?.context

        // Le backend envoie la clé de traduction complète
        if (errorCode) {
          this.error = this.$t(errorCode, errorContext || {})
        } else {
          this.error = this.$t(err.response?.data?.message || err.message)
        }

        this.notifyError(this.error)
      } finally {
        this.loading = false
      }
    },

    close() {
      this.internalValue = false
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
